import './navbar.scss';

import { Tooltip } from 'antd';
import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router';

import { AdminNavBar } from '@/components/AdminPanel/NavBar/AdminNavBar';
import {
  ADMIN_ROUTES,
  FAQ_ROUTES,
  ROUTES
} from '@/components/Shared/routes/routes';
import { NavbarBadge } from '@/components/Shared/ui';
import {
  WIDTH_FOR_OPENED_NAVBAR,
  pathAudioLinesIcon,
  pathExpandLogo,
  pathQuestionicon,
  pathServiceDeskIcon,
  pathShieldUserLogo
} from '@/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import AllurFinanceLogo from '@/media/allur-finance.svg?react';
import { useAppDispatch, useAppSelector } from '@/store';
import { onSetSelectedBankToInitial } from '@/store/financingStep.slice';
import { setSidebarExpanded } from '@/store/userInfo.slice';

const TooltipWrapper: React.FC<{
  isSidebarExpanded: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ isSidebarExpanded, title, children }) => {
  return (
    <Tooltip
      open={isSidebarExpanded ? false : undefined}
      title={title}
      placement="right"
      destroyOnHidden
    >
      {children}
    </Tooltip>
  );
};

const Navbar = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { newSecurityCount } = useAppSelector((state) => state.security);
  const { newLeadsCount } = useAppSelector((state) => state.leads);
  const {
    menu,
    isSidebarExpanded,
    userAccesses: {
      security: { hasGeneralAccessToSecurity },
      global: { isAdmin },
      speechAnalytics: { hasAccessToSpeechAnalytics }
    }
  } = useAppSelector((state) => state.userInfo);
  const { newRequestsAdminBadge, reworkRequestsAdminBadge } = useAppSelector(
    (state) => state.serviceDesk
  );

  const [windowWidth] = useWindowSize();

  const onClick = () => {
    dispatch(onSetSelectedBankToInitial());
    if (windowWidth < WIDTH_FOR_OPENED_NAVBAR)
      dispatch(setSidebarExpanded(false));
  };

  useEffect(() => {
    if (windowWidth !== 0)
      dispatch(setSidebarExpanded(windowWidth > WIDTH_FOR_OPENED_NAVBAR));
  }, [windowWidth]);

  const getBadgeForPage = {
    leads: (
      <NavbarBadge
        isSidebarExpanded={isSidebarExpanded}
        count={newLeadsCount}
      />
    ),
    serviceDesk: (
      <NavbarBadge
        isSidebarExpanded={isSidebarExpanded}
        count={newRequestsAdminBadge + reworkRequestsAdminBadge}
      />
    ),
    security: (
      <NavbarBadge
        isSidebarExpanded={isSidebarExpanded}
        count={newSecurityCount}
      />
    )
  } satisfies Record<string, React.ReactNode>;

  return (
    <>
      <div
        onClick={() => dispatch(setSidebarExpanded(false))}
        className={`navbar-overlay ${isSidebarExpanded ? 'navbar-overlay--shown' : ''}`}
      ></div>
      <aside
        className={`navbar ${
          isSidebarExpanded ? 'navbar--expanded' : 'navbar--collapsed'
        }`}
      >
        <div className="navbar__logo-box">
          <NavLink
            className="navbar__logo"
            to={ROUTES.autoCredit}
            onClick={onClick}
          >
            <AllurFinanceLogo className="navbar__logo" />
          </NavLink>
          <button
            className="navbar__arrow"
            onClick={() => dispatch(setSidebarExpanded(!isSidebarExpanded))}
          >
            <img src={pathExpandLogo} alt="logo_expand" />
          </button>
        </div>

        <nav className="navbar__navigation-box">
          {location.pathname.includes(ADMIN_ROUTES.root) ? (
            <AdminNavBar isSidebarExpanded={isSidebarExpanded} />
          ) : (
            menu.map((element, index) => {
              const isMainPage = element.code === 'main';
              const isActive =
                (isMainPage && location.pathname === ROUTES.home) ||
                location.pathname.includes(element.code);
              const href = isMainPage ? ROUTES.home : `/${element.code}`;

              const linkClasses = [
                'navbar__link',
                isMainPage ? 'navbar__link--main-page' : '',
                !isMainPage && index === 0 ? 'navbar__link--margin-top' : '',
                isActive ? 'navbar__link--active' : ''
              ].join(' ');

              return (
                <NavLink
                  to={href}
                  className={linkClasses}
                  onClick={onClick}
                  key={element.uuid}
                >
                  <TooltipWrapper
                    isSidebarExpanded={isSidebarExpanded}
                    title={element?.name}
                  >
                    <img
                      className="navbar__link-icon"
                      src={element.icon}
                      alt="setting_icon"
                    />
                  </TooltipWrapper>
                  <span className="navbar__link-text">{element?.name}</span>
                  {getBadgeForPage[
                    element.code as keyof typeof getBadgeForPage
                  ] ?? null}
                </NavLink>
              );
            })
          )}
        </nav>

        {!location.pathname.includes(ADMIN_ROUTES.root) && (
          <div className="navbar__support-setting-box">
            {hasAccessToSpeechAnalytics && (
              <a
                href="https://speech-analytics.cyberdev.app/login?access_token=v2Slh1jCJoKYll4QQ_enJ8dQ3IDK6SHcK5PUtNeXCsE"
                target="_blank"
                className="navbar__link"
                onClick={onClick}
              >
                <TooltipWrapper
                  isSidebarExpanded={isSidebarExpanded}
                  title="Речевая аналитика"
                >
                  <img
                    className="navbar__link-icon"
                    src={pathAudioLinesIcon}
                    alt="speech-analytics_icon"
                  />
                </TooltipWrapper>
                <p className="navbar__link-text">Речевая аналитика</p>
              </a>
            )}
            {isAdmin && (
              <NavLink
                to={ROUTES.serviceDesk}
                onClick={onClick}
                className={`navbar__link ${location.pathname.includes(ROUTES.serviceDesk) ? 'navbar__link--active' : ''}`}
              >
                <TooltipWrapper
                  isSidebarExpanded={isSidebarExpanded}
                  title="Service Desk"
                >
                  <img
                    className="navbar__link-icon"
                    src={pathServiceDeskIcon}
                    alt="service-desk_icon"
                  />
                </TooltipWrapper>
                <p className="navbar__link-text">Service Desk</p>
                {getBadgeForPage.serviceDesk}
              </NavLink>
            )}
            {hasGeneralAccessToSecurity && (
              <NavLink
                to={ROUTES.security}
                onClick={onClick}
                className={`navbar__link ${location.pathname.includes(ROUTES.security) ? 'navbar__link--active' : ''}`}
              >
                <TooltipWrapper
                  isSidebarExpanded={isSidebarExpanded}
                  title="Безопасность"
                >
                  <img
                    className="navbar__link-icon"
                    src={pathShieldUserLogo}
                    alt="security_icon"
                  />
                </TooltipWrapper>
                <p className="navbar__link-text">Безопасность</p>
                {getBadgeForPage.security}
              </NavLink>
            )}
            <NavLink
              to={FAQ_ROUTES.root}
              onClick={onClick}
              className={`navbar__link ${location.pathname.includes(FAQ_ROUTES.root) ? 'navbar__link--active' : ''}`}
            >
              <TooltipWrapper
                isSidebarExpanded={isSidebarExpanded}
                title="Поддержка"
              >
                <img
                  className="navbar__link-icon"
                  src={pathQuestionicon}
                  alt="support_icon"
                />
              </TooltipWrapper>
              <p className="navbar__link-text">Поддержка</p>
            </NavLink>
          </div>
        )}
      </aside>
    </>
  );
};

export default Navbar;
